// src/pages/SendPackage.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Container, Stack, useMediaQuery,
  Tabs, Tab, Snackbar, Alert, Button,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Package, Clock, Wrench, PlusCircle } from "lucide-react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";
import { useDelivery } from "../context/DeliveryContext";
import { useDeliveryForm } from "../context/DeliveryFormContext";
import { FilterChip } from "../shared/components/Chips/FilterChip";
import { DeliverySummaryModal } from "../shared/components/Modals/DeliverySummaryModal";
import { EcoOptionsModal } from "../shared/components/Modals/EcoOptionsModal";
import { RegularOptionsModal } from "../shared/components/Modals/RegularOptionsModal";
import { BusTransportModal } from "../shared/components/Modals/BusTransportModal/BusTransportModal";
import { BidModal } from "../shared/components/Modals/BidModal";
import { ItemsSelectionModal } from "../shared/components/Modals/ItemsSelectionModal";
import { PickupTimeModal } from "../shared/components/Modals/PickupTimeModal";
import { LocationAutocomplete } from "../shared/components/AutoComplete/LocationAutocomplete";
import { DeliveryStopInput } from "../shared/components/DeliveryStopInput/DeliveryStopInput";
import { DeliveryOptions } from "./SendPackagePage/DeliveryOptions";
import { MyDeliveriesList } from "./SendPackagePage/MyDeliveriesList";
import { PaymentModal } from "../modules/payments";
import { StickyFooter } from "../shared/components/Footer/StickyFooter";
import { formatPickupTimeLabel } from "../utils/timeUtils";
import { printDeliveryWaybill } from "../utils/printUtils/index";
import { getSenderReferral } from "../utils/sendShare";

// ── UIDs allowed to use multi-stop ──────────────────────────────────────────
const MULTI_STOP_ALLOWED_UIDS = [
  'Zi2F4htuAxWJ3QCTHYQhh6kOi932',
  'eST8kiI1X4hF7EONsfwPKxoSOAz1',
  'qVXBp986uGhGb65x0Rnil4e4nmA3',
  'W8lR15FfqUa2BrO6kJMIICceRKo2',
  "cxScxXMo71Png9b21faSX6YelqY2",
  "9hbHuR4ri3PRisAO1dzUgau5itx1",
  "xrYdOCtXK0WTJ2Z9MXevB9nI0vs1",
  '6oKlJG35WtOJx0asF5oqWnDSTn42',
];

const MAX_EXTRA_STOPS = 10;

// ── Helpers ──────────────────────────────────────────────────────────────────
const msToDateStr = (ms) => ms ? new Date(ms).toISOString().split("T")[0] : "";
const msToTimeStr = (ms) => ms ? new Date(ms).toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" }) : "";
const dateTimeToMs = (date, time) => date && time ? new Date(`${date}T${time}`).getTime() : null;

const makeEmptyStop = () => ({
  address: "",
  locationData: null,
  time: Date.now(),   // default to now
  hint: "",
});

export const SendPackage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getUserDeliveriesFromUserDoc } = useDelivery();
  const [searchParams, setSearchParams] = useSearchParams();
  const { shareId } = useParams();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const {
    deliveryData,
    updateDelivery,
    submitting,
    estimatedPrice,
    timeWindowFee,
    totalPrice,
    canSubmit,
    selectedTransportLabel,
    confirmDelivery,
    prefillFromDelivery,
  } = useDeliveryForm();

  const userDeliveries = getUserDeliveriesFromUserDoc();
  const tabFromUrl = searchParams.get("tab") || "send";
  const deliveryIdFromUrl = searchParams.get("deliveryId");
  const [currentTab, setCurrentTab] = useState(tabFromUrl);

  // ── Multi-stop state ─────────────────────────────────────────────────────
  const canMultiStop = user?.uid && MULTI_STOP_ALLOWED_UIDS.includes(user.uid);
  const [extraStops, setExtraStops] = useState([]);

  const handleAddStop = () => {
    if (extraStops.length >= MAX_EXTRA_STOPS) return;
    setExtraStops((prev) => [...prev, makeEmptyStop()]);
  };

  const handleStopChange = (index, updated) => {
    setExtraStops((prev) => prev.map((s, i) => (i === index ? updated : s)));
  };

  const handleRemoveStop = (index) => {
    setExtraStops((prev) => prev.filter((_, i) => i !== index));
  };

  // Build actions array from extra stops for delivery submission
  const buildActionsFromStops = useCallback(() => {
    return extraStops
      .filter((s) => s.address)
      .map((s) => ({
        kind: "deliver",
        address: s.address,
        city: s.locationData?.city ?? "",
        hint: s.hint ?? "",
        time: s.time ?? null,
        transport: "wait",
        windowEnd: null,
      }));
  }, [extraStops]);

  // ── Sender referral ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!shareId) return;
    getSenderReferral(shareId).then((ref) => {
      if (ref?.courierId) updateDelivery({ shareInfo: {
        referredBy: ref.courierId,
        shareId: ref.shareId,
      }});
    });
  }, [shareId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Modal state ──────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryMode, setSummaryMode] = useState("confirm");
  const [pickupTimeModal, setPickupTimeModal] = useState(false);
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [ecoOptionsModalOpen, setEcoOptionsModalOpen] = useState(false);
  const [regularOptionsModalOpen, setRegularOptionsModalOpen] = useState(false);
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [busTransportModalOpen, setBusTransportModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingPaymentDelivery, setPendingPaymentDelivery] = useState(null);
  const [ecoOptionData, setEcoOptionData] = useState(null);
  const [regularBasePrice, setRegularBasePrice] = useState(5);
  const [bidBasePrice, setBidBasePrice] = useState(3);

  // ── Toast ────────────────────────────────────────────────────────────────
  const showToast = useCallback((message, severity = "error") => {
    setToast({ open: true, message, severity });
  }, []);
  const handleCloseToast = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  // ── URL param geocoding ──────────────────────────────────────────────────
  useEffect(() => {
    const originParam = searchParams.get("origin");
    const destinationParam = searchParams.get("destination");
    if (!originParam && !destinationParam) return;

    const geocode = (address) => new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) return resolve(null);
      new window.google.maps.Geocoder().geocode(
        { address, componentRestrictions: { country: "fi" } },
        (results, status) => {
          if (status === "OK" && results?.[0]) {
            const r = results[0];
            resolve({
              address: r.formatted_address,
              latitude: r.geometry.location.lat(),
              longitude: r.geometry.location.lng(),
              placeId: r.place_id,
            });
          } else resolve(null);
        }
      );
    });

    const waitForGoogle = () => new Promise((resolve) => {
      if (window.google?.maps?.Geocoder) return resolve();
      const iv = setInterval(() => {
        if (window.google?.maps?.Geocoder) { clearInterval(iv); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(iv); resolve(); }, 5000);
    });

    (async () => {
      await waitForGoogle();
      if (originParam) {
        const decoded = decodeURIComponent(originParam);
        updateDelivery({ origin: decoded, originData: await geocode(decoded) });
      }
      if (destinationParam) {
        const decoded = decodeURIComponent(destinationParam);
        updateDelivery({ destination: decoded, destData: await geocode(decoded) });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab sync ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setCurrentTab(tab);
  }, [searchParams]);

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    setSearchParams(new URLSearchParams({ tab: newValue }));
  };

  // ── Transport option handlers ────────────────────────────────────────────
  const handleEcoClick = (ecoOption) => {
    if (!deliveryData.origin || !deliveryData.destination) {
      showToast(t("delivery.errors.selectAddresses"), "warning");
      return;
    }
    setEcoOptionData(ecoOption);
    const reg = ecoOption.options?.find((o) => o.id === "regular");
    const bid = ecoOption.options?.find((o) => o.id === "bid");
    if (reg) setRegularBasePrice(reg.price);
    if (bid) setBidBasePrice(bid.price);
    setEcoOptionsModalOpen(true);
  };

  const handleBusTransportConfirm = (price, busTransportData, startMs, endMs) => {
    const legs = busTransportData?.legs ?? [];
    const firstLeg = legs[0];
    const lastLeg  = legs[legs.length - 1];
    const disabledAmount = busTransportData._disabledLegIndices?.length || 0;

    updateDelivery({
      busTransportData,
      selectedOption: {
        optionId: "eco-bus",
        price,
        option: { id: "eco-bus", title: t("delivery.ecoBus"), priceType: "fixed" },
      },
      price,
      pickupTime: firstLeg?.start ?? startMs ?? null,
      targetTime: lastLeg?.end   ?? endMs   ?? null,
      ...(disabledAmount === 1 && firstLeg?.origin     && { origin:      firstLeg.origin }),
      ...(disabledAmount > 1  && lastLeg?.destination && { destination: lastLeg.destination }),
    });
    setBusTransportModalOpen(false);
  };

  const handleRegularConfirm = (data) => {
    updateDelivery({
      selectedOption: {
        optionId: "eco-regular", price: data.price,
        option: { id: "eco-regular", title: `${t("delivery.eco")} - ${t("delivery.regular")}`, priceType: "fixed" },
        regularData: data,
      },
    });
    setRegularOptionsModalOpen(false);
  };

  const handleBidConfirm = (bidData) => {
    const pickupTime = dateTimeToMs(bidData.pickupDate, bidData.pickupTime) || deliveryData.pickupTime;
    const targetTime = dateTimeToMs(bidData.targetDate, bidData.targetTime) || deliveryData.targetTime;

    updateDelivery({
      selectedOption: {
        optionId: "eco-bid",
        price: bidData.price,
        option: { id: "eco-bid", title: `${t("delivery.eco")} - ${t("delivery.bid")}`, priceType: "custom" },
      },
      pickupTime,
      targetTime,
    });
    setBidModalOpen(false);
  };

  // ── Submit flow ──────────────────────────────────────────────────────────
  const handleSubmit = () => {
    if (!user?.uid) { navigate("/create-user",
      { state: { from: { pathname: "/laheta-paketti", search: window.location.search } } }
    ); return; }
    if (!deliveryData.origin || !deliveryData.destination) {
      showToast(t("delivery.errors.selectAddresses"), "error"); return;
    }
    if (!deliveryData.selectedOption?.optionId) {
      showToast(t("delivery.errors.selectOption"), "error"); return;
    }
    setSummaryMode("confirm");
    setSummaryModalOpen(true);
  };

  const handleConfirmDelivery = async (overrideData) => {
    setSummaryModalOpen(false);
    const mergedDeliveryData = {
      ...deliveryData,
      ...(overrideData && typeof overrideData === "object" && !overrideData.nativeEvent
        ? overrideData
        : {}),
    };
    const shouldOpenPaymentModal = mergedDeliveryData.paymentMethod === "card";
    const paymentAmount = Number(totalPrice || estimatedPrice || mergedDeliveryData.selectedOption?.price || 0);
    const actions = buildActionsFromStops();
    const result = await confirmDelivery({
      ...(overrideData && typeof overrideData === "object" && !overrideData.nativeEvent
        ? overrideData
        : {}),
      ...(actions.length > 0 && {
        actions,
        deliveryType: "multi-delivery",
      }),
    });
    if (!result.success) {
      showToast(t("delivery.errors.createFailed"), "error");
      return;
    }

    showToast(t("delivery.success.created"), "success");

    if (shouldOpenPaymentModal) {
      setPendingPaymentDelivery({
        deliveryId: result.deliveryId,
        amount: paymentAmount,
        deliveryType:
          mergedDeliveryData?.selectedOption?.optionId ||
          mergedDeliveryData?.deliveryTransportType ||
          "package",
        from: mergedDeliveryData.origin,
        to: mergedDeliveryData.destination,
      });
      setPaymentModalOpen(true);
    }
  };

  const handlePaymentSuccess = useCallback(() => {
    setPaymentModalOpen(false);
    setPendingPaymentDelivery(null);
    showToast(t("delivery.paymentSuccessful") || t("payment.success"), "success");
  }, [showToast, t]);

  const handleResend = useCallback((delivery) => {
    prefillFromDelivery(delivery);
    setExtraStops([]); // clear stops on resend
    setCurrentTab("send");
    setSearchParams(new URLSearchParams({ tab: "send" }));
    window.scrollTo({ top: 0, behavior: "smooth" });
    showToast(t("delivery.resendNotification"), "info");
  }, [prefillFromDelivery, showToast, setSearchParams, t]);

  const handlePrintWaybill = useCallback((delivery) => {
    try {
      printDeliveryWaybill(delivery);
      showToast(t("delivery.waybillPrinted"), "success");
    } catch { showToast(t("delivery.errors.waybillPrintFailed"), "error"); }
  }, [showToast, t]);

  // ── Labels ───────────────────────────────────────────────────────────────
  const getPickupTimeLabel = () =>
    formatPickupTimeLabel(deliveryData.pickupTime, deliveryData.latestPickUpTime, deliveryData.hasTimeWindow, t);

  const getItemsSummary = () => {
    if (!deliveryData.items?.length) return t("delivery.selectItems");
    const totalWeight = deliveryData.items.reduce((s, i) => s + i.weight, 0);
    const category = deliveryData.items[0]?.sizeCategory || "small";
    return deliveryData.items.length === 1
      ? t(`delivery.${category}`)
      : `${deliveryData.items.length} ${t("delivery.items")} (${totalWeight}${t("delivery.kg")})`;
  };

  const showTabs = userDeliveries.length > 0;
  const userSendingDeliveries = userDeliveries.filter((d) => d.senderId === user?.uid);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pt: 10, pb: currentTab === "send" ? (isMobile ? 12 : 14) : 4 }}>
      <Container maxWidth={currentTab === "my-deliveries" ? "lg" : "sm"}>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label={t("delivery.sendNew")} value="send" />
            {showTabs && <Tab label={t("delivery.myDeliveries")} value="my-deliveries" />}
          </Tabs>
        </Box>

        {currentTab === "send" && (
          <>
            <Stack spacing={1.5}>
              <LocationAutocomplete
                label={t("delivery.pickup")}
                value={deliveryData.origin}
                onChange={(value) => updateDelivery({ origin: value })}
                onLocationSelect={(data) => updateDelivery({ originData: data })}
              />
              <LocationAutocomplete
                label={t("delivery.dropoff")}
                value={deliveryData.destination}
                onChange={(value) => updateDelivery({ destination: value })}
                onLocationSelect={(data) => updateDelivery({ destData: data })}
              />

              {/* ── Extra stops (allowed UIDs only) ── */}
              {canMultiStop && (
                <>
                  {extraStops.map((stop, index) => (
                    <DeliveryStopInput
                      key={index}
                      index={index}
                      stop={stop}
                      onChange={(updated) => handleStopChange(index, updated)}
                      onRemove={() => handleRemoveStop(index)}
                    />
                  ))}

                  {extraStops.length < MAX_EXTRA_STOPS && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<PlusCircle size={16} />}
                      onClick={handleAddStop}
                      sx={{ alignSelf: "flex-start", color: "text.secondary" }}
                    >
                      Lisää useita toimitusosoitteita
                    </Button>
                  )}
                </>
              )}
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <FilterChip icon={Clock} label={getPickupTimeLabel()} selected={deliveryData.hasTimeWindow} onClick={() => setPickupTimeModal(true)} />
                <FilterChip icon={Package} label={getItemsSummary()} selected={deliveryData.items.length > 1} onClick={() => setItemsModalOpen(true)} />
                <FilterChip icon={Wrench} label={t("delivery.moreDetails")} selected={!!deliveryData.senderPhone} onClick={() => { setSummaryMode("edit"); setSummaryModalOpen(true); }} />
              </Stack>
            </Box>

            {deliveryData.distance && (
              <Box sx={{ mt: 2 }}>
                <DeliveryOptions
                  km={deliveryData.distance}
                  durationMin={deliveryData.duration}
                  items={deliveryData.items}
                  destData={deliveryData.destData}
                  originData={deliveryData.originData}
                  value={deliveryData.selectedOption}
                  onChange={(value) => updateDelivery({ selectedOption: value })}
                  defaultOptionId="express"
                  onEcoClick={handleEcoClick}
                  pickupTime={deliveryData.pickupTime}
                  targetTime={deliveryData.targetTime}
                  hasTimeWindow={deliveryData.hasTimeWindow}
                />
              </Box>
            )}
          </>
        )}

        {currentTab === "my-deliveries" && (
          <MyDeliveriesList
            deliveries={userSendingDeliveries}
            highlightDeliveryId={deliveryIdFromUrl}
            onResend={handleResend}
            onResendConfirm={handleConfirmDelivery}
            handlePrintWaybill={handlePrintWaybill}
          />
        )}

        <EcoOptionsModal
          open={ecoOptionsModalOpen} onClose={() => setEcoOptionsModalOpen(false)}
          ecoOptions={ecoOptionData?.options || []}
          onSelectBus={() => { setEcoOptionsModalOpen(false); setBusTransportModalOpen(true); }}
          onSelectRegular={() => { setEcoOptionsModalOpen(false); setRegularOptionsModalOpen(true); }}
          onSelectBid={() => { setEcoOptionsModalOpen(false); setBidModalOpen(true); }}
        />
        <RegularOptionsModal open={regularOptionsModalOpen} onClose={() => setRegularOptionsModalOpen(false)} basePrice={regularBasePrice} onConfirm={handleRegularConfirm} />
        <BidModal
          open={bidModalOpen}
          onClose={() => setBidModalOpen(false)}
          onConfirm={handleBidConfirm}
          basePrice={bidBasePrice}
          defaultPickupDate={msToDateStr(deliveryData.pickupTime)}
          defaultPickupTime={msToTimeStr(deliveryData.pickupTime)}
          defaultTargetDate={msToDateStr(deliveryData.targetTime)}
          defaultTargetTime={msToTimeStr(deliveryData.targetTime)}
        />
        <BusTransportModal open={busTransportModalOpen} onClose={() => setBusTransportModalOpen(false)} origin={deliveryData.origin} destination={deliveryData.destination} pickupTime={deliveryData.pickupTime} onConfirm={handleBusTransportConfirm} />
      </Container>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={handleCloseToast} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: "100%", boxShadow: 3 }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <PickupTimeModal
        open={pickupTimeModal} onClose={() => setPickupTimeModal(false)}
        currentPickupTime={deliveryData.pickupTime} currentTargetTime={deliveryData.targetTime}
        currentLatestPickUpTime={deliveryData.latestPickUpTime} currentEarliestTarget={deliveryData.earliestTargetTime}
        hasTimeWindow={deliveryData.hasTimeWindow}
        onConfirm={(data) => { updateDelivery(data); setPickupTimeModal(false); }}
      />

      <ItemsSelectionModal open={itemsModalOpen} onClose={() => setItemsModalOpen(false)} title={t("delivery.selectPackageItems")} currentItems={deliveryData.items} onSave={(items) => updateDelivery({ items })} />

      {deliveryData.distance && currentTab === "send" && (
        <StickyFooter
          estimatedPrice={totalPrice} onSubmit={handleSubmit} canSubmit={canSubmit} submitting={submitting}
          priceLabel={t("delivery.estimatedPrice")} submitLabel={t("delivery.createDelivery")}
          submittingLabel={t("delivery.creatingDelivery")} selectedTransportLabel={selectedTransportLabel}
        />
      )}

      <DeliverySummaryModal
        open={summaryModalOpen} onClose={() => setSummaryModalOpen(false)}
        onConfirm={handleConfirmDelivery} delivery={deliveryData}
        onUpdateField={(field, value) => updateDelivery({ [field]: value })}
        onEditBusOption={() => setBusTransportModalOpen(true)}
        estimatedPrice={estimatedPrice} timeWindowFee={timeWindowFee}
        totalPrice={totalPrice} selectedTransportLabel={selectedTransportLabel}
        mode={summaryMode}
      />

      {pendingPaymentDelivery && (
        <PaymentModal
          open={paymentModalOpen}
          amount={pendingPaymentDelivery.amount}
          onSuccess={handlePaymentSuccess}
          onClose={() => {
            setPaymentModalOpen(false);
            setPendingPaymentDelivery(null);
          }}
          title={t("delivery.payForDelivery") || t("payment.completePayment")}
          metadata={{
            deliveryId: pendingPaymentDelivery.deliveryId,
            deliveryType: pendingPaymentDelivery.deliveryType,
            from: pendingPaymentDelivery.from,
            to: pendingPaymentDelivery.to,
          }}
        />
      )}
    </Box>
  );
};
